// Small picker modal (styled like the Bulk Edit popup) used by the New Page Details create
// form. Opened by the "Search Parent" / "Search Post Category" button next to the Parent Page
// field. Loads its options via `load` and returns the chosen value through onSelect — the caller
// writes it into the Parent/Category field. Generic: value = page slug (Parent) or category name.

import { useState, useEffect, type CSSProperties } from "react";
import ParentSelect from "./ParentSelect";

interface Props {
  title: string;                 // e.g. "Search Parent" / "Search Post Category"
  label: string;                 // dropdown label, e.g. "Parent Page" / "Post Category"
  load: () => Promise<Array<{ value: string; label: string }>>;
  onClose: () => void;
  onSelect: (value: string) => void;
}

export function ParentPickerDialog({ title, label, load, onClose, onSelect }: Props) {
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [value, setValue] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr("");
    load()
      .then(o => { if (alive) { setOptions(o); setLoading(false); } })
      .catch(() => { if (alive) { setErr("Couldn't load the list from WordPress."); setLoading(false); } });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lt = document.documentElement.getAttribute("data-theme") !== "dark";
  const panelBg = lt ? "#ffffff" : "#0f172a";
  const border = lt ? "1px solid #e2e8f0" : "1px solid rgba(255,255,255,.12)";
  const textColor = lt ? "#0f172a" : "#e2e8f0";
  const labelColor = lt ? "#64748b" : "rgba(255,255,255,.6)";
  const inputBorder = lt ? "1px solid #cbd5e1" : "1px solid rgba(255,255,255,.15)";

  const labelStyle: CSSProperties = { display: "block", fontSize: 10, fontWeight: 700, letterSpacing: .6, textTransform: "uppercase", color: labelColor, fontFamily: "'Inter','Mulish',sans-serif", marginBottom: 5 };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onMouseDown={onClose}
    >
      <div
        style={{ width: 560, maxWidth: "100%", background: panelBg, border, borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.45)", padding: 22, fontFamily: "'Inter','Mulish',sans-serif" }}
        onMouseDown={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 17, fontWeight: 800, color: textColor, marginBottom: 16 }}>{title}</div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>{label}</label>
          {loading ? (
            <div style={{ fontSize: 13, color: labelColor }}>Loading…</div>
          ) : err ? (
            <div style={{ fontSize: 13, color: "#dc2626" }}>{err}</div>
          ) : options.length === 0 ? (
            <div style={{ fontSize: 13, color: labelColor }}>Nothing found on the connected site.</div>
          ) : (
            <ParentSelect
              value={value}
              onChange={setValue}
              theme={lt ? "light" : "dark"}
              placeholder="— Select —"
              options={[{ value: "", label: "— Select —" }, ...options]}
            />
          )}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 16px", background: lt ? "#fff" : "transparent", color: lt ? "#475569" : "#e2e8f0", border: inputBorder, borderRadius: 0, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" }}>Cancel</button>
          <button
            onClick={() => value && onSelect(value)}
            disabled={!value}
            style={{ padding: "9px 18px", background: value ? "linear-gradient(135deg,#15803d,#16a34a)" : "#93c5a3", color: "#fff", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 700, cursor: value ? "pointer" : "not-allowed", fontFamily: "'Inter','Mulish',sans-serif" }}
          >Select</button>
        </div>
      </div>
    </div>
  );
}
